import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import i18n from 'i18next'
import clsx from 'clsx'

import Footer from '@/components/Layout/Footer'
import Header from '@/components/PublicHeader/Header'
import ScrollToTop from '@/components/ScrollToTop'
import useAdobeAnalysis from '@/hooks/useAdobeAnalysis'
import useDocumentTitle from '@/hooks/useDocumentTitle'
import { PAGE_TYPES } from '@/libs/constants/adobeAnalytics'
import { parseHtml } from '@/libs/utils/common'

import styles from './DataProtection.module.scss'

const DataProtection = () => {
  useDocumentTitle('pageTitle.dataProtection')
  const language = localStorage.getItem('lng')
  const { t } = useTranslation()

  useEffect(() => {
    if (globalThis.OneTrust) {
      globalThis.OneTrust.initializeCookiePolicyHtml(true)
    }
  }, [language])

  useAdobeAnalysis(null, {
    pageName: 'data protection policy',
    pageId: '30',
    pageType: PAGE_TYPES.POLICY,
  })

  const renderSection = (index) => (
    <section key={`section-${index}`} className="data-policy-section">
      {parseHtml(
        t(`dataProtectionPolicy.section${index}`).replaceAll(/>\s+</g, '><'),
      )}
    </section>
  )

  const renderContent = () => {
    const mainContentKey = 'dataProtectionPolicy.mainContent'

    // If mainContent doesn't exist, fall back to rendering all sections sequentially
    if (!i18n.exists(mainContentKey)) {
      const sections = []
      let i = 1
      while (i18n.exists(`dataProtectionPolicy.section${i}`)) {
        sections.push(renderSection(i))
        i += 1
      }
      sections.push(<div key="ot-sdk-cookie-policy" id="ot-sdk-cookie-policy" />)
      sections.push(
        <button
          key="ot-sdk-btn"
          className={clsx('ot-sdk-show-settings', styles.settingButton)}
          id="ot-sdk-btn"
          type="button"
        >
          {t('dataProtectionPolicy.cookieSettings')}
        </button>,
      )
      return sections
    }

    // Parse the mainContent to extract the order
    const mainContent = t(mainContentKey)
    // Match patterns like {{Section1}}, {{CookieButton}}, {{CookieTable}}
    const orderMatches = mainContent.match(/\{\{(\w+)\}\}/g)

    if (!orderMatches) {
      // Fallback if no matches found
      const sections = []
      let i = 1
      while (i18n.exists(`dataProtectionPolicy.section${i}`)) {
        sections.push(renderSection(i))
        i += 1
      }
      return sections
    }

    const content = []

    // Process each placeholder in order
    for (const match of orderMatches) {
      const key = match.replaceAll(/\{\{|\}\}/g, '') // Extract key from {{Key}}

      if (key.startsWith('Section')) {
        const sectionNumber = Number.parseInt(key.replace('Section', ''), 10)
        if (i18n.exists(`dataProtectionPolicy.section${sectionNumber}`)) {
          content.push(renderSection(sectionNumber))
        }
      } else if (key === 'CookieButton') {
        content.push(
          <button
            key="ot-sdk-btn"
            className={clsx('ot-sdk-show-settings', styles.settingButton)}
            id="ot-sdk-btn"
            type="button"
          >
            {t('dataProtectionPolicy.cookieSettings')}
          </button>,
        )
      } else if (key === 'CookieTable') {
        content.push(<div key="ot-sdk-cookie-policy" id="ot-sdk-cookie-policy" />)
      }
    }

    return content
  }

  return (
    <>
      <Header />
      <ScrollToTop />
      <div className={styles.wrapper}>
        <div className={styles.containerWidth}>
          <div>
            {renderContent()}
          </div>
        </div>
      </div>
      <Footer />
    </>
  )
}

export default DataProtection
